import { PrivateKey } from "@bsv/sdk";
import type {
	BapMasterBackup,
	BapMasterBackupLegacy,
	DecryptedBackup,
	MasterBackupType42,
} from "bitcoin-backup";
import { encryptBackup, isLegacyBackup, isType42Backup } from "bitcoin-backup";
import { BAP, type LegacyIdRecompute, type MasterID } from "bsv-bap";
import { markCloudBackupStale } from "@/lib/bap/cloud-refresh";
import {
	type IdentityBackup,
	isSeedIdentityBackup,
	validateIdentityBackup,
} from "@/lib/bap/identity-backup";
import { persistReissuedBackupOffer } from "@/lib/bap/reissued-backup";
import type { SeedBackup } from "@/lib/bap/seed-contract";
import { SeedProfileAdapter } from "@/lib/bap/seed-profile-adapter";
import { assertValidLegacyBackup } from "@/lib/bap/validate-backup";
import { STORAGE_KEYS } from "@/lib/storage-keys";

export interface ExtractedIdentity {
	id: string;
	privateKey: string;
	pubkey: string;
	address: string;
	bapId: string;
}

export interface ReissuedBackup {
	/** Upgraded backup with ids re-exported under the current derivation. */
	backup: BapMasterBackup;
	/** Old → new bapId mapping for each recomputed identity. */
	recomputed: LegacyIdRecompute[];
	/**
	 * The re-encrypted backup string. Returned so callers can re-offer the
	 * download from a user gesture if the automatic download was blocked
	 * (e.g. when reissue runs outside user activation, like an auto-submit).
	 */
	encrypted: string;
	/** Suggested filename for the reissued backup file. */
	filename: string;
}

/**
 * Detect a pre-0.3 legacy backup (bsv-bap 0.3 changed identity derivation
 * and is intentionally NOT backward compatible), recompute its identities
 * under the current scheme — same keys, NEW bapIds — then persist the
 * re-encrypted backup and download a reissued backup file. The old file
 * is obsolete afterward.
 *
 * Returns null when the backup is already current-format (the common case).
 */
export async function reissueLegacyBackupIfNeeded(
	backup: BapMasterBackup,
	password: string
): Promise<ReissuedBackup | null> {
	assertValidLegacyBackup(backup);
	if (!("ids" in backup && backup.ids) || backup.ids.length === 0) {
		return null;
	}

	let bap: BAP;
	if (isLegacyBackup(backup)) {
		bap = new BAP(backup.xprv);
	} else if (isType42Backup(backup)) {
		bap = new BAP({ rootPk: backup.rootPk });
	} else {
		return null;
	}

	if (!bap.isLegacyIdsExport(backup.ids)) {
		return null;
	}

	const recomputed = bap.recomputeLegacyIds(backup.ids);
	const upgraded: BapMasterBackup = { ...backup, ids: bap.exportIds() };

	const encrypted = await encryptBackup(upgraded, password);
	localStorage.setItem(STORAGE_KEYS.ENCRYPTED_BACKUP, encrypted);
	markCloudBackupStale();

	const label = ("label" in upgraded && upgraded.label) || "wallet";
	const filename = `${String(label).replaceAll(" ", "-").toLowerCase()}-reissued.bep`;

	// Persist the reissue offer so a durable banner on the destination page
	// (onboarding/account) can surface the download. The login page hard-
	// navigates after sign-in, which tears down any toast it raises — the
	// persisted offer survives the navigation within the same tab (OPL-2518).
	persistReissuedBackupOffer({
		encrypted,
		filename,
		recomputedCount: recomputed.length,
		mappings: recomputed.map(({ oldBapId, newBapId, rootPath }) => ({
			oldBapId,
			newBapId,
			rootPath,
		})),
	});

	// Attempt the download immediately too. This may be silently blocked when
	// reissue runs outside a user gesture (e.g. the auto-submitted login
	// form), so the persisted offer + banner is the reliable path; callers
	// also get `encrypted`/`filename` back to re-offer from a user gesture.
	downloadFile(encrypted, filename, "text/plain");

	return { backup: upgraded, recomputed, encrypted, filename };
}

/**
 * Initialize BAP instance from a backup with identities imported
 * Returns null for member/WIF backups, which cannot manage identity trees.
 */
export function initializeBAPFromBackup(
	backup: DecryptedBackup | IdentityBackup
): BAP | null {
	validateIdentityBackup(backup);
	if (isSeedIdentityBackup(backup)) {
		return null;
	}
	assertValidLegacyBackup(backup);
	let bap: BAP;

	if (isLegacyBackup(backup)) {
		bap = new BAP(backup.xprv);
	} else if (isType42Backup(backup)) {
		bap = new BAP({ rootPk: backup.rootPk });
	} else {
		return null;
	}

	// Import existing identities if they exist
	if (backup.ids && backup.ids.length > 0) {
		bap.importIds(backup.ids);
	}

	return bap;
}

/**
 * Get the account key WIF for an identity.
 * The account key is the root-derived key that the BAP ID is computed from.
 * TODO: Group 3 — replace with BRC-100 wallet signing key lookup
 * (query BAP basket for highest sequence, read derivation from custom instructions)
 */
export function getAccountWif(identity: {
	getAccountKey(): PrivateKey;
}): string {
	return identity.getAccountKey().toWif();
}

/**
 * Get the account key's public key for an identity.
 * TODO: Group 3 — replace with BRC-100 wallet signing key lookup
 */
export function getAccountPubkey(identity: {
	getAccountKey(): PrivateKey;
}): string {
	return identity.getAccountKey().toPublicKey().toString();
}

function assertCounter(counter: number): void {
	if (
		!Number.isSafeInteger(counter) ||
		counter < 0 ||
		counter > 2_147_483_647
	) {
		throw new Error(
			"Identity counter must be an integer between 0 and 2147483647"
		);
	}
}

function verifyTarget(identity: MasterID, targetBapId?: string): string {
	if (targetBapId !== undefined && identity.bapId !== targetBapId) {
		throw new Error("Derived identity does not match the requested BAP ID");
	}
	return getAccountWif(identity);
}

/**
 * Extract WIF from master backup using counter (bypasses backup's ids field)
 * This is the clean way - database has the counter, we just derive it
 *
 * @param backup - Master backup (legacy xprv or Type42 rootPk)
 * @param counter - Identity counter from database
 * @returns WIF private key string
 */
function extractSeedWif(
	backup: SeedBackup,
	bapId?: string,
	counter?: number
): string {
	if (counter !== undefined) {
		assertCounter(counter);
	}
	const profile =
		counter === undefined
			? backup.profiles.find(
					(entry) => entry.bapId === (bapId ?? backup.profiles[0].bapId)
				)
			: backup.profiles.find((entry) => entry.index === counter);
	if (!profile || (bapId !== undefined && profile.bapId !== bapId)) {
		throw new Error("Requested seed profile is not in this backup");
	}
	const identity = new SeedProfileAdapter(backup).getId(profile.bapId);
	if (!identity) {
		throw new Error("Requested seed profile is not in this backup");
	}
	return getAccountWif(identity);
}

export function extractWifFromBackupByCounter(
	backup: DecryptedBackup | IdentityBackup,
	counter: number,
	targetBapId?: string
): string {
	validateIdentityBackup(backup);
	assertCounter(counter);
	if (isSeedIdentityBackup(backup)) {
		return extractSeedWif(backup, targetBapId, counter);
	}
	assertValidLegacyBackup(backup);
	if (isType42Backup(backup)) {
		const bap = new BAP({ rootPk: backup.rootPk });
		return verifyTarget(bap.newIdWithCounter(counter), targetBapId);
	}
	if (isLegacyBackup(backup)) {
		const bap = new BAP(backup.xprv);
		if (backup.ids.length > 0) {
			bap.importIds(backup.ids);
		}
		const allIds = bap.listIds();
		let identity: MasterID | null;
		if (counter < allIds.length) {
			identity = bap.getId(allIds[counter]);
		} else if (allIds.length === 0 && !bap.lastIdPath) {
			// With no inventory, repeated newId() uses exactly this canonical path.
			identity = bap.newId(`/0'/${counter}'/0'`);
		} else if (counter === allIds.length) {
			identity = bap.newId();
		} else {
			// Imported inventories may have custom paths, removals, or idSeeds.
			// Do not run an unbounded generation loop or guess missing positions.
			throw new Error(
				"Restore the identity inventory before deriving a missing legacy counter"
			);
		}
		if (!identity) {
			throw new Error("Failed to derive the requested identity");
		}
		return verifyTarget(identity, targetBapId);
	}
	throw new Error(
		"extractWifFromBackupByCounter requires a master backup (legacy or Type42)"
	);
}

/**
 * Extract WIF from a Type42 master backup.
 */
function extractWifFromType42(
	backup: MasterBackupType42,
	targetBapId: string | undefined
): string {
	const bap = new BAP({ rootPk: backup.rootPk });

	if (backup.ids && backup.ids.length > 0) {
		bap.importIds(backup.ids);
	}

	const allIds = bap.listIds();

	let resolvedBapId = targetBapId;
	if (resolvedBapId === undefined) {
		if (allIds.length === 0) {
			return getAccountWif(bap.newId());
		}
		resolvedBapId = allIds[0];
	}

	const member = bap.getId(resolvedBapId);
	if (!member) {
		throw new Error("Requested identity was not found in this backup");
	}

	return getAccountWif(member);
}

/**
 * Extract WIF from a legacy (xprv) master backup.
 */
function extractWifFromLegacy(
	backup: BapMasterBackupLegacy,
	targetBapId: string | undefined
): string {
	const bap = new BAP(backup.xprv);

	if (backup.ids && backup.ids.length > 0) {
		bap.importIds(backup.ids);
	}

	const allIds = bap.listIds();
	let resolvedBapId = targetBapId;
	if (resolvedBapId === undefined) {
		if (allIds.length === 0) {
			const newIdentity = bap.newId();
			resolvedBapId = newIdentity.bapId;
		} else {
			resolvedBapId = allIds[0];
		}
	}

	const member = bap.getId(resolvedBapId);
	if (!member) {
		throw new Error("Requested identity was not found in this backup");
	}

	return getAccountWif(member);
}

/**
 * Extract WIF private key from backup for a specific BAP ID
 *
 * @param backup - Any backup format
 * @param bapId - Optional BAP ID to extract. Only applies to master backups (xprv/rootPk).
 *                Member backups already have a fixed identity.
 * @param counter - Optional counter to derive identity (bypasses backup lookup)
 * @returns WIF private key string
 */
export function extractWifFromBackup(
	backup: DecryptedBackup | IdentityBackup,
	bapId?: string,
	counter?: number
): string {
	validateIdentityBackup(backup);
	if (isSeedIdentityBackup(backup)) {
		return extractSeedWif(backup, bapId, counter);
	}
	assertValidLegacyBackup(backup);
	if (counter !== undefined) {
		assertCounter(counter);
	}
	if ("wif" in backup) {
		if (counter !== undefined) {
			throw new Error("Member backups do not support identity counters");
		}
		if (bapId !== undefined && (!("id" in backup) || backup.id !== bapId)) {
			throw new Error("Requested identity was not found in this member backup");
		}
		return backup.wif;
	}
	// Counter derivation must also match an explicitly requested identity.
	if (
		counter !== undefined &&
		(isLegacyBackup(backup) || isType42Backup(backup))
	) {
		return extractWifFromBackupByCounter(backup, counter, bapId);
	}

	if (isType42Backup(backup)) {
		return extractWifFromType42(backup, bapId);
	}

	if (isLegacyBackup(backup)) {
		return extractWifFromLegacy(backup, bapId);
	}

	throw new Error("Unsupported backup format");
}

/**
 * Get the first BAP ID from a BAP instance, creating one if needed.
 */
function getFirstOrCreateBapId(bap: InstanceType<typeof BAP>): string {
	const ids = bap.listIds();
	if (ids.length === 0) {
		const newIdentity = bap.newId();
		return newIdentity.bapId;
	}
	return ids[0];
}

/**
 * Resolve the BAP identity key for a given backup.
 * If bapId is provided, returns it directly for master backups.
 * Otherwise resolves the first identity from the backup.
 */
function resolveIdentityKey(
	backup: DecryptedBackup | IdentityBackup,
	bapId: string | undefined
): string {
	if (isSeedIdentityBackup(backup)) {
		return bapId ?? backup.profiles[0].bapId;
	}
	if ("wif" in backup) {
		if ("id" in backup) {
			return backup.id;
		}
		throw new Error("A bare WIF backup has no bound BAP identity");
	}
	if (bapId !== undefined) {
		return bapId;
	}
	if (isType42Backup(backup)) {
		const bap = new BAP({ rootPk: backup.rootPk });
		if (backup.ids && backup.ids.length > 0) {
			bap.importIds(backup.ids);
		}
		return getFirstOrCreateBapId(bap);
	}
	if (isLegacyBackup(backup)) {
		const bap = new BAP(backup.xprv);
		if (backup.ids && backup.ids.length > 0) {
			bap.importIds(backup.ids);
		}
		return getFirstOrCreateBapId(bap);
	}
	throw new Error("Unknown backup format");
}

/**
 * Extract identity information from a backup
 * @param backup - Any backup format
 * @param bapId - Optional specific BAP ID to extract. If not provided, uses first/root identity
 * @returns Complete identity information including WIF, pubkey, address, and BAP ID
 */
export function extractIdentityFromBackup(
	backup: DecryptedBackup | IdentityBackup,
	bapId?: string
): ExtractedIdentity {
	// Get the WIF for this identity (handles all backup types)
	const wif = extractWifFromBackup(backup, bapId);

	// Derive pubkey and address from WIF
	const privateKey = PrivateKey.fromWif(wif);
	const pubkey = privateKey.toPublicKey();
	const address = pubkey.toAddress();

	const resolvedBapId = resolveIdentityKey(backup, bapId);

	return {
		id: resolvedBapId,
		privateKey: wif,
		pubkey: pubkey.toString(),
		address: address.toString(),
		bapId: resolvedBapId,
	};
}

/**
 * Download a file to the user's browser
 */
export function downloadFile(
	content: string,
	filename: string,
	mimeType = "text/plain"
): void {
	const blob = new Blob([content], { type: mimeType });
	const url = URL.createObjectURL(blob);
	const a = document.createElement("a");
	a.href = url;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	// Defer cleanup: revoking the object URL (or removing the anchor)
	// synchronously after click races the browser's download and can abort
	// it before it starts. Let the click dispatch first.
	setTimeout(() => {
		a.remove();
		URL.revokeObjectURL(url);
	}, 0);
}

/**
 * Download an encrypted backup file
 * Follows the pattern from login page for consistent backup file handling
 *
 * IMPORTANT: Also updates localStorage and marks backup as downloaded
 * to prevent false "backup changed" notifications
 *
 * ⚠️  CRITICAL: This function ALWAYS uses password encryption (bitcoin-backup).
 * NEVER use PRF encryption for exported files - they would be unrecoverable
 * without the original device's passkey.
 *
 * Password encryption is portable and can be decrypted on any device.
 * PRF encryption is device-bound and for localStorage only.
 *
 * @see lib/bap/backup.ts header comments for full architecture explanation
 */
export async function downloadEncryptedBackup(
	backup: IdentityBackup,
	password: string,
	filename?: string
): Promise<void> {
	const snapshot = JSON.stringify(validateIdentityBackup(backup));
	const sourceLocal = localStorage.getItem(STORAGE_KEYS.ENCRYPTED_BACKUP);
	const sourceSession = sessionStorage.getItem(STORAGE_KEYS.DECRYPTED_BACKUP);
	const { syncBackupToStorage, markBackupAsDownloaded } = await import(
		"@/lib/bap/backup"
	);
	if (
		localStorage.getItem(STORAGE_KEYS.ENCRYPTED_BACKUP) !== sourceLocal ||
		sessionStorage.getItem(STORAGE_KEYS.DECRYPTED_BACKUP) !== sourceSession
	) {
		throw new Error("Backup download was superseded");
	}
	const result = await syncBackupToStorage(
		validateIdentityBackup(JSON.parse(snapshot)),
		password,
		{
			requirePortable: true,
			deferCloudFlush: true,
		}
	);
	if (!(result.success && result.portablePersisted)) {
		throw new Error(result.error || "Could not save portable backup");
	}
	if (sessionStorage.getItem(STORAGE_KEYS.DECRYPTED_BACKUP) !== snapshot) {
		throw new Error("Backup download was superseded");
	}
	const encrypted = localStorage.getItem(STORAGE_KEYS.ENCRYPTED_BACKUP);
	if (!encrypted) {
		throw new Error("Portable backup is unavailable");
	}
	const defaultFilename = `bap-backup-${new Date().toISOString().split("T")[0]}.bep`;
	downloadFile(encrypted, filename || defaultFilename, "text/plain");
	await markBackupAsDownloaded();
}
